/**
 * Trip Tracer selection cascade (Design Review "FE Mock Cascade").
 *
 * Each entry lists the selections a change to that field clears. The portal
 * agency change sits above this table: it resets the whole slice. Product
 * changes to the reset rules are edits to this table only.
 */
export const TRIP_TRACER_QUERY_RESETS = Object.freeze({
    month: ['serviceDate', 'startTime', 'endTime', 'vehicleId', 'routeId'],
    serviceDate: ['startTime', 'endTime', 'vehicleId', 'routeId'],
    // Switching vehicle-first ↔ route-first drops the incompatible selection.
    mode: ['vehicleId', 'routeId'],
    startTime: [],
    endTime: [],
    vehicleId: [],
    routeId: [],
});

/**
 * Loaded data and the selections it was loaded for. Data is cleared whenever
 * any of its selections is cleared or changed, so a stale result is never
 * shown while the next one loads.
 */
export const TRIP_TRACER_CATALOG_KEYS = Object.freeze({
    avlDates: ['month'],
    vehicles: ['serviceDate'],
    routes: ['serviceDate'],
    routeShape: ['mode', 'routeId'],
    replay: ['serviceDate', 'mode', 'vehicleId', 'routeId', 'startTime', 'endTime'],
});

/** Replay one vehicle, or every vehicle on a route. */
export const TRIP_TRACER_QUERY_MODE = Object.freeze({
    VEHICLE: 'vehicle',
    ROUTE: 'route',
});

export const getTripTracerQueryInitialState = () => ({
    mode: TRIP_TRACER_QUERY_MODE.VEHICLE,
    month: null,
    serviceDate: null,
    startTime: null,
    endTime: null,
    vehicleId: null,
    routeId: null,
});

/**
 * Sets one selection and clears what depends on it.
 *
 * @param {Object} query
 * @param {string} field - A key of TRIP_TRACER_QUERY_RESETS
 * @param {*} value
 * @returns {Object} The next query (the same object when nothing changed)
 */
export const applyTripTracerSelection = (query, field, value) => {
    if (!Object.prototype.hasOwnProperty.call(TRIP_TRACER_QUERY_RESETS, field)) {
        throw new Error(`Unknown Trip Tracer selection: ${field}`);
    }

    if (query[field] === value) {
        return query;
    }

    const cleared = Object.fromEntries(TRIP_TRACER_QUERY_RESETS[field].map((key) => [key, null]));

    return { ...query, ...cleared, [field]: value };
};

/**
 * Whether a change from `previous` to `next` query invalidates loaded data.
 *
 * @param {string} catalog - A key of TRIP_TRACER_CATALOG_KEYS
 * @param {Object} previous
 * @param {Object} next
 * @returns {boolean}
 */
export const isTripTracerCatalogStale = (catalog, previous, next) => (
    TRIP_TRACER_CATALOG_KEYS[catalog].some((key) => previous[key] !== next[key])
);
